import { useMemo, useState, useEffect } from 'react';
import { Box, Text } from '@chakra-ui/react';
import {
    ColumnDef,
    SortingFn,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { useGetRetailerByClientQuery } from 'core/API/authApi';
import { useGetCompanyInfoSelector, useGetUserInfoSelector } from 'core/redux/selectors/authSelectors';
import { SORTING_FNS } from 'plugins/DITable/helpers/sort';
import { useTranslation } from 'helpers/languages/translations';
import DefaultColumnFilter from 'plugins/DITable/filters/DefaultColumnFilter';
import { useGetColorsQuery } from 'core/API/ColorsApi';
import { IColor, IOptionalColor, TColorTypes } from 'types';
import { linkStyle } from 'shared/styles';

export const DEFAULT_COLOR = '#2C7691';
export const colorTypes: TColorTypes[] = ['retailer', 'company', 'category', 'sub-category', 'segment'];

const useColors = () => {
    const [languages, setLanguages] = useState<string[]>([]);
    const [filters, setFilters] = useState<{ id: string; value: unknown }[]>([]);
    const { data: retailers, isLoading: isRetailersLoading, isError: isRetailerError } = useGetRetailerByClientQuery();
    const [modalType, setModalType] = useState<'add' | 'update'>('add');
    const [selectedType, setSelectedType] = useState<TColorTypes>('retailer');
    const [showColorPicker, setShowColorPicker] = useState<boolean>(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const translate = useTranslation();
    const [colorToSubmit, setColorToSubmit] = useState<IOptionalColor>({
        color: DEFAULT_COLOR,
        type: selectedType,
    });

    const {
        data: colorsData,
        isError: isColorsError,
        isLoading: isColorsLoading,
    } = useGetColorsQuery({ unset: false, type: selectedType });

    const {
        data: unsetColorsData,
        isError: isUnsetColorsError,
        isLoading: isUnsetColorsLoading,
    } = useGetColorsQuery({ unset: true, type: selectedType });

    const companyInfo = useGetCompanyInfoSelector();
    const userInfo = useGetUserInfoSelector();

    useEffect(() => {
        if (!isRetailersLoading && !isRetailerError && retailers) {
            const langs: Set<string> = new Set<string>();
            retailers.data.forEach((retailer) => {
                if (retailer.languages) {
                    retailer.languages.forEach((l: string) => {
                        if (l !== '') langs.add(l);
                    });
                }
            });

            const langsArr = Array.from(langs);
            setLanguages(langsArr);
        }
    }, [retailers, isRetailersLoading]);

    const data: Readonly<IColor[]> = useMemo(() => {
        if (!isColorsLoading && !isColorsError && colorsData) {
            return colorsData.colors;
        }
        return [];
    }, [isColorsLoading, isColorsError, colorsData, selectedType]);

    const caseInsensitiveSort = useMemo(() => SORTING_FNS.caseInsensitive, []);
    const sortTypes: Record<string, SortingFn<any>> = useMemo(
        () => ({
            ...SORTING_FNS,
        }),
        [],
    );

    const defaultColumn = useMemo(
        () => ({
            meta: {
                Filter: DefaultColumnFilter,
            },
        }),
        [],
    );

    const handleOpenModal = (type: 'add' | 'update') => {
        if (type === 'add') setShowColorPicker(true);
        else if (type === 'update') setShowColorPicker(false);
        setModalType(type);
        setIsModalOpen(true);
    };

    const columns: Readonly<ColumnDef<IColor, any>[]> = useMemo(() => {
        const tableColumns: readonly (ColumnDef<IColor, any> | null)[] = [
            {
                header: () => translate('colors.column.name') as string,
                accessorKey: 'name',
                sortingFn: 'caseInsensitive',
                meta: {
                    width: '20%',
                },
                cell: (info) => {
                    const cellValue = info.row.original as IColor;
                    return (
                        <Box
                            onClick={() => {
                                setColorToSubmit({
                                    object_id: cellValue.object_id,
                                    type: cellValue.type,
                                    color_id: cellValue.id,
                                    color: cellValue.color,
                                    name: cellValue.name,
                                });
                                handleOpenModal('update');
                            }}
                        >
                            <Text {...linkStyle}>{cellValue.name}</Text>
                        </Box>
                    );
                },
            } as ColumnDef<IColor, any>,

            {
                header: () => translate('colors.column.color') as string,
                accessorKey: 'color',
                sortingFn: 'caseInsensitive',
                meta: {
                    width: '20%',
                },
                cell: (info) => {
                    const cellValue = info.row.original as IColor;
                    return (
                        <Box display="flex">
                            <Box
                                w="20px"
                                h="20px"
                                borderRadius="50%"
                                bg={cellValue.color}
                                border={cellValue.color.toLowerCase() === '#ffffff' ? '1px solid #E2E8F0' : 'none'}
                                mr="4px"
                                display="inline-block"
                            ></Box>
                            <Text>{cellValue.color}</Text>
                        </Box>
                    );
                },
            } as ColumnDef<IColor, any>,
        ];

        const filteredColumns = tableColumns.filter((e): e is ColumnDef<IColor, any> => e !== null);
        return filteredColumns;
    }, [colorsData, languages, userInfo, selectedType]);

    const table = useReactTable<IColor>({
        columns: columns as ColumnDef<IColor, any>[],
        data: data as IColor[],
        defaultColumn,
        sortingFns: sortTypes,
        initialState: { columnFilters: filters },
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    useEffect(() => {
        setFilters(table.getState().columnFilters);
    }, [table.getState().columnFilters]);

    const handleChangeColorType = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const colorType = e.target.value as TColorTypes;
        setSelectedType(colorType);
    };

    const props = useMemo(
        () => ({
            languages,
            filters,
            retailers,
            isRetailersLoading,
            isRetailerError,
            selectedType,
            colorToSubmit,
            columns,
            table,
            colorsData,
            isColorsError,
            isColorsLoading,
            unsetColorsData,
            isUnsetColorsError,
            isUnsetColorsLoading,
            colorTypes,
            companyInfo,
            userInfo,
            defaultColumn,
            data,
            sortTypes,
            modalType,
            isModalOpen,
            showColorPicker,
            setSelectedType,
            setColorToSubmit,
            setLanguages,
            caseInsensitiveSort,
            handleChangeColorType,
            handleOpenModal,
            setModalType,
            setIsModalOpen,
            setShowColorPicker,
        }),
        [
            languages,
            filters,
            retailers,
            isRetailersLoading,
            isRetailerError,
            selectedType,
            colorToSubmit,
            columns,
            table,
            colorsData,
            isColorsError,
            isColorsLoading,
            unsetColorsData,
            isUnsetColorsError,
            isUnsetColorsLoading,
            colorTypes,
            companyInfo,
            userInfo,
            defaultColumn,
            data,
            sortTypes,
            modalType,
            isModalOpen,
            showColorPicker,
        ],
    );

    return props;
};

export type TColorsHooks = ReturnType<typeof useColors>;

export default useColors;
